import React, { useState, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import {
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TableHead,
    Button,
    ButtonGroup,
} from '@mui/material'
import SourceApi from '../../../../api/SourceApi'
import { getValidSourceTypes, getValidUploadedSourceTypes } from '../../../../utils/issues/sourceTypes'
import { intersection } from './utils'
import { SourceEntity } from 'types/SourceEntity'
import { UploadedSourceDialog } from './AdminUploadedSourceDialog'
import { SourceFormRow } from './AdminSourceFormRow'
import { SourceTableRow } from './AdminSourceTableRows'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { isBefore } from 'date-fns'
import { FormIssueEntity } from '../../types/FormIssueEntity'
import { FormWarrantEntity } from '../../types/FormWarrantEntity'
import { SourceForm } from '../../types/SourceForm'
import { FormConvertibleEntity } from '../../types/FormConvertibleEntity'
import { SourceInternalTableRow } from './AdminSourceInternalTableRow'
import { IssueType, TransactionType, WarrantListItem } from '@finance-lab/utils/types'

export type SourceInputMethod = 'internal' | 'manual'

const allowUploadedSource = (issueType: IssueType) => {
    return intersection(getValidUploadedSourceTypes(), getValidSourceTypes('Issue', issueType)).length > 0
}

const nullEmptyStrings = (obj: Record<string, unknown>) => {
    return Object.keys(obj).reduce(
        (acc, key) => ({
            ...acc,
            [key]: obj[key] === '' ? null : obj[key],
        }),
        {},
    )
}

function SourcesFields({
    isIssue,
    warrant,
    convertible,
    allowUpload = true,
    setIsCustomFormDirty,
}: {
    isIssue?: boolean
    warrant?: WarrantListItem
    convertible?: ConvertibleEntity
    allowUpload?: boolean
    setIsCustomFormDirty: (val: boolean) => void
}) {
    const [duplicateSources, setDuplicateSources] = useState({})
    const [inputMethod, setInputMethod] = useState<SourceInputMethod>('internal')
    const { setValue, watch } = useFormContext<FormIssueEntity | FormWarrantEntity | FormConvertibleEntity>()
    const entity = watch()

    const sources = entity.sources
    const [openUploadDialog, setOpenUploadDialog] = useState(false)
    const sourceApi = new SourceApi()

    const setFormSources = useCallback(
        (sources: Array<SourceForm>) => {
            setValue('sources', sources)
        },
        [setValue],
    )

    if (!sources) return <LinearProgress />

    const handleUpdateSource = (source: SourceForm) => {
        const newSources = [...sources]
        const index = newSources.findIndex((s) => {
            if (source.id) return s.id === source.id
            if (source.sourceUrl) return s.sourceUrl === source.sourceUrl

            return s.prId === source.prId
        })
        newSources[index] = { ...newSources[index], ...source }
        setFormSources(newSources)

        return true
    }

    const handleDelete = (source: Partial<Pick<SourceEntity, 'sourceUrl' | 'prId'> & { id?: string }>) => {
        setFormSources(
            sources.filter((s) => {
                if (source.id) return s.id !== source.id

                return source.sourceUrl !== s.sourceUrl
            }),
        )
    }

    const handleSubmit = (sourceForm: SourceForm) => {
        const source = nullEmptyStrings(sourceForm) as Omit<SourceEntity, 'id'> & SourceForm
        if (isIssue) source.issueId = entity.id
        if (warrant) source.warrantId = warrant.id
        if (convertible) source.convertibleId = convertible.id
        const sourceAlreadyExists = sources.some((s) => {
            if (source.sourceUrl) return source.sourceUrl === s.sourceUrl
            if (source.prId) return source.prId === s.prId

            return false
        })
        if (sourceAlreadyExists) {
            console.error('Source already added')
            return
        }
        setIsCustomFormDirty(true)
        setFormSources([source, ...sources])

        const uniqueKey = source.sourceUrl ? 'sourceUrl' : source.prId ? 'prId' : null

        if (uniqueKey) {
            sourceApi
                .findByField({ [uniqueKey]: source[uniqueKey] })
                .then((sources) => {
                    if (sources.length > 0) {
                        const key = source[uniqueKey] as string
                        setDuplicateSources({ ...duplicateSources, [key]: sources })
                    }
                })
                .catch((err) => console.error(err))
        }
    }
    const handleUploadSubmit = async (uploadUrl: string, file: File, data: SourceForm) => {
        if (!allowUpload) return
        const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/pdf' },
            body: file,
        })
        if (uploadResponse.ok) {
            console.log('Upload successful')
            void handleSubmit(data)
        } else {
            console.error('Upload failed')
        }
    }

    allowUpload = allowUpload && 'type' in entity && allowUploadedSource(entity?.type)
    return (
        <React.Fragment>
            <ButtonGroup sx={{ marginRight: 4, marginLeft: 4 }}>
                <Button
                    variant={inputMethod === 'internal' ? 'contained' : 'outlined'}
                    onClick={() => setInputMethod('internal')}
                >
                    Intern källa
                </Button>
                <Button
                    variant={inputMethod === 'manual' ? 'contained' : 'outlined'}
                    onClick={() => setInputMethod('manual')}
                >
                    Manuell inmatning
                </Button>
            </ButtonGroup>
            {allowUpload && (
                <>
                    <UploadedSourceDialog
                        open={openUploadDialog}
                        setOpen={setOpenUploadDialog}
                        handleSubmit={handleUploadSubmit}
                        issue={isIssue ? (entity as FormIssueEntity) : null}
                        publicationDate={entity?.publicationDate || warrant?.publicationDate || null}
                        subStartDate={watch('subStartDate')}
                        warrant={warrant}
                    />
                    <Button variant='outlined' onClick={() => setOpenUploadDialog(true)} sx={{ marginRight: 2 }}>
                        Ny uppladdad källa
                    </Button>
                </>
            )}

            <TableContainer>
                <Table sx={{ paddingBottom: 4 }}>
                    <TableBody>
                        <SourceFormRow
                            inputMethod={inputMethod}
                            onSubmit={handleSubmit}
                            issue={isIssue ? (entity as FormIssueEntity) : undefined}
                            warrant={warrant}
                            convertible={convertible}
                            sources={sources}
                        />
                    </TableBody>
                </Table>
            </TableContainer>

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>URL</TableCell>
                            <TableCell>Kommentar</TableCell>
                            <TableCell>Typ</TableCell>
                            <TableCell>Titel</TableCell>
                            <TableCell>Publicerad</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sources
                            .sort((a, b) =>
                                isBefore(new Date(a.publishedDate ?? 0), new Date(b.publishedDate ?? 0)) ? -1 : 1,
                            )
                            .map((source) => {
                                const issueType = isIssue ? (entity as FormIssueEntity).type : null
                                const transactionType = (
                                    isIssue ? (entity as FormIssueEntity).type : warrant ? 'Warrant' : 'Convertible'
                                ) as TransactionType

                                return source.prId ? (
                                    <SourceInternalTableRow
                                        key={source.prId}
                                        issueType={issueType}
                                        onUpdate={handleUpdateSource}
                                        onDelete={handleDelete}
                                        source={source as Extract<SourceForm, { prId: string }>}
                                        duplicateSources={duplicateSources}
                                        transactionType={transactionType}
                                        issue={isIssue ? (entity as FormIssueEntity) : null}
                                    />
                                ) : (
                                    <SourceTableRow
                                        key={source.id || source.sourceUrl}
                                        issueType={issueType}
                                        source={source}
                                        sources={sources}
                                        onUpdate={handleUpdateSource}
                                        onDelete={handleDelete}
                                        duplicateSources={duplicateSources}
                                        transactionType={transactionType}
                                        issue={isIssue ? (entity as FormIssueEntity) : null}
                                    />
                                )
                            })}
                    </TableBody>
                </Table>
            </TableContainer>
        </React.Fragment>
    )
}

export { SourcesFields }
